import { AnalyticsEvents, captureAnalyticsEvent } from "@/analytics";
import { logout } from "@/auth/auth";
import { useCreateOrganization, useInviteUser, useOrganizationInvitations, useOrganizationMembers, useRemoveOrganizationMember, useRevokeInvitation, useSendSupportMessage, useSetOrganizationMemberRole, useUpdateOrganization } from "@/client/backend/organization";
import { useAcceptInvitation, useDeleteOIDCConnection, useOIDCConnection, useOrganization, useOrganizationDomains, usePutOIDCConnection, usePutOrganizationDomains, useVerifyOrganizationDomain } from "@/client/backend/organization.tsx";
import { keyLifetimeOptions, useCreateAPIKey, useOrganizationAPIKeys, useDeleteAPIKey } from "@/client/backend/api-key";
import { ShownOnceSecret } from "@/components/copy";
import { useUpdateUser, useUser } from "@/client/backend/user";
import type { components } from "@/client/backend/zz_schema";
import { AppShell, PageHeader } from "@/components/layout/AppShell";
import { InstanceWebhookSettings } from "@/components/settings/instance-webhook";
import { NodePools } from "@/components/settings/node-pools";
import { SCIMProvisioning, useSCIMConfiguration } from "@/components/settings/scim-provisioning";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { keyScopeBadge, refreshWhoAmI, seatCreatedOrganization, useOrganizationScopes } from "@/lib/scopes";
import { handleApiError } from "@/utils/api";
import { formatErrorMessage } from "@/utils/format";
import { ReloadIcon } from "@radix-ui/react-icons";
import { Copy, Lock, MinusCircle, PlusCircle, UserIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useSWRConfig } from 'swr';


type Section = "profile" | "organization" | "sso" | "nodePools" | "support" | "tokens" | "webhooks";
type OrganizationDomainDraft = {
  domain: string;
  autoJoinEnabled: boolean;
};

const personalEmailDomains = new Set([
  "aol.com",
  "fastmail.com",
  "gmail.com",
  "googlemail.com",
  "hey.com",
  "hotmail.com",
  "icloud.com",
  "live.com",
  "mac.com",
  "mail.com",
  "me.com",
  "msn.com",
  "outlook.com",
  "pm.me",
  "proton.me",
  "protonmail.com",
  "yahoo.com",
]);

export type SettingsLinkProps = {
  initialSection?: Section;
};

const roleLabels: Record<components["schemas"]["Role"], string> = { admin: "Admin", member: "Member", viewer: "Viewer" };
const roleDescriptions: Record<components["schemas"]["Role"], string> = {
  admin: "Full access, including billing, organization settings, and member management.",
  member: "Create and use instances. Manage assets, secrets, and API keys. No billing or organization management.",
  viewer: "View instances, assets, analytics, and members. Cannot control instances or make changes.",
};

function RoleHelp() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-auto px-2 py-1 text-xs text-muted-foreground">
          About roles
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)]" aria-label="Organization roles">
        <dl className="space-y-3 text-sm">
          {(["admin", "member", "viewer"] as const).map((role) => (
            <div key={role}>
              <dt className="font-medium">{roleLabels[role]}</dt>
              <dd className="mt-1 text-muted-foreground">{roleDescriptions[role]}</dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Says why a card has no controls: the person's role may read it but not
 * change it. Controls are hidden rather than disabled, so this is the only
 * explanation the page gives and it names who can help.
 */
function ReadOnlyNotice({ role }: { role: components["schemas"]["Role"] | undefined }) {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
      <Lock className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {role ? `You are a ${roleLabels[role].toLowerCase()} in this organization, so this is read-only.` : "This is read-only for your role."}{" "}
        Ask an organization admin to make changes or to change your role.
      </span>
    </div>
  );
}

/** One row of the members card: a person or an open invitation, with its actions on the right. */
function SeatRow({ title, subtitle, dashed, children }: { title: string; subtitle: string; dashed?: boolean; children: React.ReactNode }) {
  return (
    <div className={`flex items-center justify-between space-x-4 rounded-lg border p-4${dashed ? " border-dashed" : ""}`}>
      <div className="flex items-center space-x-4">
        <div className="rounded-full bg-secondary p-2">
          <UserIcon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium leading-none">{title}</p>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/** One selector for every place a role is chosen; `roles` narrows the choice. */
function RoleSelect({
  value,
  onChange,
  roles = ["admin", "member", "viewer"],
  disabled,
  className = "w-32",
  id,
  "aria-label": ariaLabel,
}: {
  value: components["schemas"]["Role"] | undefined;
  onChange: (role: components["schemas"]["Role"]) => void;
  roles?: components["schemas"]["Role"][];
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as components["schemas"]["Role"])} disabled={disabled}>
      <SelectTrigger id={id} className={className} aria-label={ariaLabel}>
        <SelectValue placeholder="Select a role" />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => (
          <SelectItem key={role} value={role}>
            {roleLabels[role]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function Settings() {
  const { toast } = useToast()
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { initialSection } = (location.state as SettingsLinkProps) ?? {};
  const { trigger: acceptInvitation } = useAcceptInvitation();

  const { user } = useUser();
  const [section, setSection] = useState<Section>(initialSection ?? "profile");
  const [organization, setOrganization] = useOrganization();
  const organizationId = organization === "loading" ? "loading" : organization.id;
  // What the person's role in this organization may do decides which
  // sections show and which controls are live; the server refuses the rest.
  const { can, denied, role, features, loaded: scopesLoaded } = useOrganizationScopes(organization);
  const canManageOrganization = can("organization", "all");
  const { data: scimConfiguration } = useSCIMConfiguration(organizationId, canManageOrganization);
  const scimConfigured = scimConfiguration?.configured === true;
  const canManageMembers = can("member", "all") && !scimConfigured;
  const canManageRoles = can("member", "all") && !scimConfiguration?.directRoleAssignmentsDisabled;
  const canManageKeys = can("apikey", "all");
  // Controls render only for roles that may change the resource; a role that
  // may only read gets the card's content plus one notice saying why.
  const readOnlyNotice = (resource: string) => denied(resource, "all") && <ReadOnlyNotice role={role} />;
  const visibleSections: Record<Section, boolean> = {
    profile: true,
    support: true,
    organization: can("organization", "read"),
    sso: can("organization", "read"),
    // Staff turn node pools on for an organization by giving it a pool limit.
    nodePools: can("nodepool", "read") && organization !== "loading" && organization.nodePoolLimit > 0,
    tokens: can("apikey", "read"),
    webhooks: scopesLoaded && role === "admin" && canManageOrganization,
  };

  useEffect(() => {
    if (
      organization === "loading" ||
      !searchParams.has("checkout_cancelled_price_id")
    ) {
      return;
    }

    captureAnalyticsEvent(AnalyticsEvents.BILLING_CHECKOUT_CANCELLED_RETURNED, {});
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("checkout_cancelled_price_id");
    setSearchParams(nextSearchParams, { replace: true });
  }, [organization, searchParams, setSearchParams]);

  // A section the role may not read falls back to the profile, once the
  // role is known; a guess would bounce an admin off the page they opened.
  const sectionVisible = visibleSections[section];
  useEffect(() => {
    if (organization !== "loading" && scopesLoaded && !sectionVisible) {
      setSection("profile");
    }
  }, [organization, scopesLoaded, section, sectionVisible]);
  // Local state to edit platform-specific default inactivity timeouts (minutes)
  const [defaultAndroidInactivityTimeout, setDefaultAndroidInactivityTimeout] = useState<number | "loading">("loading");
  const [defaultIosInactivityTimeout, setDefaultIosInactivityTimeout] = useState<number | "loading">("loading");
  const [defaultXcodeInactivityTimeout, setDefaultXcodeInactivityTimeout] = useState<number | "loading">("loading");

  // Local state for editable Concurrency Limit
  const [concurrencyLimit, setConcurrencyLimit] = useState<number | "loading">("loading");

  // Local states for organization display name and description
  const [orgDisplayName, setOrgDisplayName] = useState<string | "loading">("loading");
  const [orgDescription, setOrgDescription] = useState<string | "loading">("loading");
  const [defaultJoinRole, setDefaultJoinRole] = useState<components["schemas"]["JoinRole"] | "loading">("loading");

  const [oidcIssuerUrl, setOIDCIssuerUrl] = useState("");
  const [oidcClientId, setOIDCClientId] = useState("");
  const [oidcClientSecret, setOIDCClientSecret] = useState("");
  const [organizationDomains, setOrganizationDomains] = useState<OrganizationDomainDraft[]>([]);
  const [newOrganizationDomain, setNewOrganizationDomain] = useState("");
  const [organizationDomainDialogRecord, setOrganizationDomainDialogRecord] = useState<
    components["schemas"]["OrganizationDomain"] | undefined
  >(undefined);
  const [isOrganizationDomainDialogOpen, setIsOrganizationDomainDialogOpen] = useState(false);
  const [isSavingOrganizationDomains, setIsSavingOrganizationDomains] = useState(false);
  const [isSavingOIDCConnection, setIsSavingOIDCConnection] = useState(false);
  const [isDeletingOIDCConnection, setIsDeletingOIDCConnection] = useState(false);
  const [verifyingOIDCDomain, setVerifyingOIDCDomain] = useState<string | null>(null);
  const [copiedOIDCRedirect, setCopiedOIDCRedirect] = useState(false);
  const [copiedOIDCPostLogoutRedirect, setCopiedOIDCPostLogoutRedirect] = useState(false);
  const [copiedOrganizationDomainField, setCopiedOrganizationDomainField] = useState<"name" | "value" | null>(null);

  // Centralized effect to reset ALL local state when organization changes
  useEffect(() => {
    if (organization !== "loading") {
      setDefaultAndroidInactivityTimeout(organization.defaultAndroidInactivityTimeoutMinutes);
      setDefaultIosInactivityTimeout(organization.defaultIosInactivityTimeoutMinutes);
      setDefaultXcodeInactivityTimeout(organization.defaultXcodeInactivityTimeoutMinutes);
      setConcurrencyLimit(organization.concurrencyLimit);
      setOrgDisplayName(organization.displayName);
      setOrgDescription(organization.description ?? "");
      setDefaultJoinRole(organization.defaultJoinRole);
    }
  }, [organization]);

  // Use inactivity timeout directly from organization object

  const [selectedDefaultOrganization, setSelectedDefaultOrganization] = useState<
    components["schemas"]["Organization"] | "loading"
  >("loading");
  if (user !== "loading" && selectedDefaultOrganization === "loading") {
    setSelectedDefaultOrganization(user.defaultOrganization);
  }
  const [selectedUserDisplayName, setSelectedUserDisplayName] = useState<string | "loading">("loading");
  if (user !== "loading" && selectedUserDisplayName === "loading" && user.displayName) {
    setSelectedUserDisplayName(user.displayName);
  }

  const { trigger: updateUser } = useUpdateUser();
  const { trigger: updateOrganization, isMutating: isUpdatingOrganization } = useUpdateOrganization(organizationId);
  const { data: oidcConnectionResult, mutate: mutateOIDCConnection } = useOIDCConnection(organizationId);
  const { data: organizationDomainsResult, mutate: mutateOrganizationDomains } = useOrganizationDomains(organizationId);
  const { trigger: putOIDCConnection } = usePutOIDCConnection(organizationId);
  const { trigger: putOrganizationDomains } = usePutOrganizationDomains(organizationId);
  const { trigger: deleteOIDCConnection } = useDeleteOIDCConnection(organizationId);
  const { trigger: verifyOrganizationDomain } = useVerifyOrganizationDomain(organizationId);

  const [supportMessage, setSupportMessage] = useState<string | undefined>(undefined);
  const [supportMessageLoading, setSupportMessageLoading] = useState<boolean>(false);
  const { trigger: sendSupportMessage } = useSendSupportMessage(organizationId);
  const [supportMessageSubmissionResult, setSupportMessageSubmissionResult] = useState<string | undefined>(undefined);

  // State for accepting invitations
  const [isAcceptingInvitation, setIsAcceptingInvitation] = useState(false);
  const [acceptingOrgId, setAcceptingOrgId] = useState<string | null>(null);

  const [dialogToken, setDialogToken] = useState<string>();

  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { tokens = [] } = useOrganizationAPIKeys(
    organization === "loading" ? undefined : organization.id
  );
  const { trigger: createToken } = useCreateAPIKey(organizationId);
  const { trigger: revokeToken } = useDeleteAPIKey(organizationId);

  const formatDateLong = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const { trigger: createOrganization } = useCreateOrganization();

  const [isCreateOrgDialogOpen, setIsCreateOrgDialogOpen] = useState(false);
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);

  const { mutate } = useSWRConfig();

  const { data: members } = useOrganizationMembers(organizationId);
  const { data: invitations } = useOrganizationInvitations(organizationId);
  const { trigger: revokeInvitation } = useRevokeInvitation(organizationId);

  const [emailToAddToOrg, setEmailToAddToOrg] = useState("");
  const [inviteRole, setInviteRole] = useState<components["schemas"]["Role"]>("member");
  const { trigger: setMemberRole } = useSetOrganizationMemberRole(organizationId);
  const [changingRoleOf, setChangingRoleOf] = useState<string | undefined>(undefined);
  const handleSetMemberRole = async (emailAddress: string, role: components["schemas"]["Role"]) => {
    if (organization === "loading") return;
    setChangingRoleOf(emailAddress);
    try {
      const result = await setMemberRole({ emailAddress, role });
      if (handleApiError(result, toast, "Role updated.")) {
        await Promise.all([
          mutate(`/organizations/${organization.id}/members`),
          // Only my own seat changes what I may do here.
          user !== "loading" && user.emailAddress === emailAddress ? refreshWhoAmI() : undefined,
        ]);
      }
    } catch (error) {
      toast({
        title: "Failed to change role",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setChangingRoleOf(undefined);
    }
  };
  const { trigger: inviteUser } = useInviteUser(organizationId);

  // State and handler for inviting members
  const [isInviting, setIsInviting] = useState(false);
  // The server stores the normalized address, so the typed one is compared lowercased.
  const isInvited = invitations?.data?.some((i) => i.email === emailToAddToOrg.trim().toLowerCase()) ?? false;

  const handleInviteMember = async () => {
    if (organization === "loading" || !emailToAddToOrg.trim()) return;
    setIsInviting(true);
    try {
      const result = await inviteUser({
        email: emailToAddToOrg.trim(),
        ...(features.roles ? { role: inviteRole } : {}),
      });
      handleApiError(result, toast, "Invitation sent successfully.");
    } catch (error) {
      toast({
        title: "Failed to send invitation",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  // Handler for accepting invitation
  const handleAcceptInvitation = async (organizationId: string) => {
    setIsAcceptingInvitation(true);
    setAcceptingOrgId(organizationId);
    try {
      const response = await acceptInvitation({ organizationId });

      if (response?.response.ok) {
        toast({
          title: "Invitation accepted!",
          description: "You have successfully joined the organization.",
        });

        // Refresh user data to get updated organizations
        await Promise.all([
          user !== "loading" ? mutate(`/users/${user.name}/memberships`) : undefined,
          refreshWhoAmI(),
        ]);
      } else {
        throw new Error("Failed to accept invitation");
      }
    } catch (error) {
      toast({
        title: "Failed to accept invitation",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsAcceptingInvitation(false);
      setAcceptingOrgId(null);
    }
  };

  const { trigger: removeMember } = useRemoveOrganizationMember(organizationId);

  // Helper to save changes programmatically (button click or Enter key)
  const handleSaveOrganization = async () => {
    // Enter in any field reaches here too; a role that may only read stops.
    if (organization === "loading" || isUpdatingOrganization || !canManageOrganization) return;
    const orgUpdate: components["schemas"]["OrganizationMutable"] = {
      concurrencyLimit: concurrencyLimit !== "loading" ? concurrencyLimit : undefined,
      displayName: orgDisplayName !== "loading" ? orgDisplayName : undefined,
      description: orgDescription !== "loading" ? orgDescription : undefined,
      defaultAndroidInactivityTimeoutMinutes: defaultAndroidInactivityTimeout !== "loading" ? defaultAndroidInactivityTimeout : undefined,
      defaultIosInactivityTimeoutMinutes: defaultIosInactivityTimeout !== "loading" ? defaultIosInactivityTimeout : undefined,
      defaultXcodeInactivityTimeoutMinutes: defaultXcodeInactivityTimeout !== "loading" ? defaultXcodeInactivityTimeout : undefined,
      // Sent only while roles are on: the API refuses the field otherwise.
      defaultJoinRole: features.roles && defaultJoinRole !== "loading" && defaultJoinRole !== organization.defaultJoinRole ? defaultJoinRole : undefined,
    };

    const result = await updateOrganization(orgUpdate);
    if (handleApiError(result, toast, "Organization updated.")) {
      if (result?.data) {
        setOrganization(result.data);
        setConcurrencyLimit(result.data.concurrencyLimit);
        setDefaultAndroidInactivityTimeout(result.data.defaultAndroidInactivityTimeoutMinutes);
        setDefaultIosInactivityTimeout(result.data.defaultIosInactivityTimeoutMinutes);
        setDefaultXcodeInactivityTimeout(result.data.defaultXcodeInactivityTimeoutMinutes);
        setOrgDisplayName(result.data.displayName);
        setOrgDescription(result.data.description ?? "");
      }
      if (user !== "loading") {
        await mutate(`/users/${user.name}/memberships`);
      }
    }
  };

  // Domain part of the current user's email, used as a helpful placeholder.
  const userEmailDomain =
    user !== "loading" && user.emailAddress.includes("@")
      ? user.emailAddress.split("@")[1].toLowerCase()
      : "";
  const suggestedOrganizationDomain =
    userEmailDomain
    && !personalEmailDomains.has(userEmailDomain)
    && !organizationDomains.some((domain) => domain.domain === userEmailDomain)
      ? userEmailDomain
      : "";
  // Only a role that may verify domains is offered the suggestion.
  const offerSuggestedDomain = suggestedOrganizationDomain !== "" && canManageOrganization;
  const normalizedNewOrganizationDomain = newOrganizationDomain.trim().toLowerCase().replace(/^@/, "");
  const selectedOrganizationDomain = normalizedNewOrganizationDomain
    ? organizationDomainDialogRecord?.domain === normalizedNewOrganizationDomain
      ? organizationDomainDialogRecord
      : organizationDomainsResult?.data?.find((domain) => domain.domain === normalizedNewOrganizationDomain)
    : undefined;
  const isSelectedOrganizationDomainVerified =
    selectedOrganizationDomain?.verifiedAt !== null && selectedOrganizationDomain?.verifiedAt !== undefined;
  const selectedOrganizationDomainHostPrefix = selectedOrganizationDomain?.verificationTxtName.split(".")[0] ?? "";

  useEffect(() => {
    if (oidcConnectionResult?.data) {
      setOIDCIssuerUrl(oidcConnectionResult.data.issuerUrl);
      setOIDCClientId(oidcConnectionResult.data.clientId);
      setOIDCClientSecret("");
      return;
    }
    if (oidcConnectionResult?.response.status === 404) {
      setOIDCIssuerUrl("");
      setOIDCClientId("");
      setOIDCClientSecret("");
    }
  }, [oidcConnectionResult?.data, oidcConnectionResult?.response.status]);

  useEffect(() => {
    if (organizationDomainsResult?.data) {
      setOrganizationDomains(organizationDomainsResult.data.map((domain) => ({
        domain: domain.domain,
        autoJoinEnabled: domain.autoJoinEnabled,
      })));
      return;
    }
    if (organizationDomainsResult?.response.status === 404) {
      setOrganizationDomains([]);
    }
  }, [organizationDomainsResult?.data, organizationDomainsResult?.response.status]);

  const handleOpenAddOrganizationDomainDialog = () => {
    setNewOrganizationDomain("");
    setOrganizationDomainDialogRecord(undefined);
    setIsOrganizationDomainDialogOpen(true);
  };

  const handleOpenVerifyOrganizationDomainDialog = (domain: string) => {
    setNewOrganizationDomain(domain);
    setOrganizationDomainDialogRecord(organizationDomainsResult?.data?.find((domainState) => domainState.domain === domain));
    setIsOrganizationDomainDialogOpen(true);
  };

  const handleOpenSuggestedOrganizationDomainDialog = () => {
    if (!suggestedOrganizationDomain) return;
    setNewOrganizationDomain(suggestedOrganizationDomain);
    setOrganizationDomainDialogRecord(undefined);
    setIsOrganizationDomainDialogOpen(true);
  };

  const handleAddOrganizationDomain = async () => {
    if (organization === "loading" || isSavingOrganizationDomains) return;
    const domain = normalizedNewOrganizationDomain;
    if (!domain) {
      return;
    }
    if (organizationDomains.some((d) => d.domain === domain)) {
      setOrganizationDomainDialogRecord(organizationDomainsResult?.data?.find((domainState) => domainState.domain === domain));
      return;
    }
    const domains = [...organizationDomains, { domain }];
    setIsSavingOrganizationDomains(true);
    try {
      const result = await putOrganizationDomains({ domains });
      if (handleApiError(result, toast, "Domain saved.")) {
        if (result?.data) {
          setOrganizationDomains(result.data.map((savedDomain) => ({
            domain: savedDomain.domain,
            autoJoinEnabled: savedDomain.autoJoinEnabled,
          })));
          setOrganizationDomainDialogRecord(result.data.find((savedDomain) => savedDomain.domain === domain));
        }
        setNewOrganizationDomain(domain);
        await mutateOrganizationDomains();
      }
    } catch (error) {
      toast({
        title: "Failed to save domain",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSavingOrganizationDomains(false);
    }
  };

  const handleRemoveOrganizationDomain = async (domain: string) => {
    if (organization === "loading" || isSavingOrganizationDomains) return;
    const domains = organizationDomains.filter((d) => d.domain !== domain);
    setIsSavingOrganizationDomains(true);
    try {
      const result = await putOrganizationDomains({ domains });
      if (handleApiError(result, toast, "Domain removed.")) {
        if (result?.data) {
          setOrganizationDomains(result.data.map((savedDomain) => ({
            domain: savedDomain.domain,
            autoJoinEnabled: savedDomain.autoJoinEnabled,
          })));
        } else {
          setOrganizationDomains(domains);
        }
        if (organizationDomainDialogRecord?.domain === domain) {
          setOrganizationDomainDialogRecord(undefined);
        }
        await mutateOrganizationDomains();
        if (user !== "loading") {
          await mutate(`/users/${user.name}/memberships`);
        }
      }
    } catch (error) {
      toast({
        title: "Failed to remove domain",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSavingOrganizationDomains(false);
    }
  };

  const handleSaveOIDCConnection = async () => {
    if (organization === "loading" || isSavingOIDCConnection) return;
    setIsSavingOIDCConnection(true);
    try {
      const body: components["schemas"]["OIDCConnectionMutable"] = {
        issuerUrl: oidcIssuerUrl.trim(),
        clientId: oidcClientId.trim(),
      };
      if (oidcClientSecret.trim()) {
        body.clientSecret = oidcClientSecret.trim();
      }
      const result = await putOIDCConnection(body);
      if (handleApiError(result, toast, "SSO connection saved.")) {
        setOIDCClientSecret("");
        await mutateOIDCConnection();
      }
    } catch (error) {
      toast({
        title: "Failed to save SSO connection",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSavingOIDCConnection(false);
    }
  };

  const handleDeleteOIDCConnection = async () => {
    if (organization === "loading" || isDeletingOIDCConnection) return;
    setIsDeletingOIDCConnection(true);
    try {
      const result = await deleteOIDCConnection();
      if (handleApiError(result, toast, "SSO connection deleted.")) {
        await mutateOIDCConnection();
      }
    } catch (error) {
      toast({
        title: "Failed to delete SSO connection",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingOIDCConnection(false);
    }
  };

  const handleVerifyOrganizationDomain = async (domain: string) => {
    setVerifyingOIDCDomain(domain);
    try {
      const result = await verifyOrganizationDomain({ domain });
      if (handleApiError(result, toast, "Domain verified.")) {
        if (result?.data) {
          setOrganizationDomains((domains) => domains.map((draft) => (
            draft.domain === result.data.domain
              ? { domain: result.data.domain, autoJoinEnabled: result.data.autoJoinEnabled }
              : draft
          )));
          setOrganizationDomainDialogRecord(result.data);
          setNewOrganizationDomain(result.data.domain);
        }
        await mutateOrganizationDomains();
        setIsOrganizationDomainDialogOpen(false);
        if (user !== "loading") {
          await mutate(`/users/${user.name}/memberships`);
        }
      }
    } catch (error) {
      toast({
        title: "Failed to verify domain",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setVerifyingOIDCDomain(null);
    }
  };

  const handleCopyOrganizationDomainField = async (field: "name" | "value", value: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedOrganizationDomainField(field);
    setTimeout(() => setCopiedOrganizationDomainField(null), 2000);
  };

  const oidcRedirectUrl = oidcConnectionResult?.data?.redirectUrl ?? `${import.meta.env.VITE_API_ENDPOINT}/authn/oidc/callback`;
  const fallbackPostLogoutRedirectUrl = new URL("/authn/login", window.location.origin);
  const oidcPostLogoutRedirectUrl = oidcConnectionResult?.data?.postLogoutRedirectUrl ?? fallbackPostLogoutRedirectUrl.toString();

  const content = (section: Section) => {
    switch (section) {
      case "profile":
        return (
          <div className="grid gap-6">
            <Card variant="section" x-chunk="dashboard-04-chunk-1">
              <CardHeader>
                <CardTitle>{user !== "loading" ? (user.displayName ?? user.name) : ""}'s Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">Name</Label>
                <Input
                  defaultValue={selectedUserDisplayName !== "loading" ? selectedUserDisplayName : undefined}
                  placeholder={selectedUserDisplayName !== "loading" ? selectedUserDisplayName : "John"}
                  className="mb-4"
                  onChange={(e) => {
                    setSelectedUserDisplayName(e.target.value);
                  }}
                />
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">Email</Label>
                <Input placeholder={user !== "loading" ? user.emailAddress : ""} disabled className="mb-4" />
                <Label className="mb-2 block text-sm font-medium text-muted-foreground">Default Organization</Label>
                <Select
                  value={selectedDefaultOrganization !== "loading" ? selectedDefaultOrganization.id : undefined}
                  onValueChange={(value) => {
                    const selected =
                      user !== "loading" ? user.organizations.find((org) => org.id === value) : undefined;
                    if (selected) {
                      setSelectedDefaultOrganization(selected);
                    }
                  }}
                >
                  <SelectTrigger className="md:max-w-[300px] md:min-w-[240px]">
                    <SelectValue
                      placeholder={
                        selectedDefaultOrganization !== "loading"
                          ? selectedDefaultOrganization.displayName
                          : "Loading..."
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Organizations</SelectLabel>
                      {(user !== "loading" ? user.organizations : []).map((org) => (
                        <SelectItem value={org.id} key={org.id}>
                          {org.displayName}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </CardContent>
              <CardFooter className="border-t px-6 py-4 flex gap-2">
                <Button
                  type="submit"
                  onClick={async () => {
                    const result = await updateUser({
                      displayName: selectedUserDisplayName !== "loading" ? selectedUserDisplayName : undefined,
                      defaultOrganizationId: selectedDefaultOrganization !== "loading" ? selectedDefaultOrganization.id : undefined,
                    });
                    handleApiError(result, toast, "Profile updated successfully.");
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={logout}
                >
                  Sign out
                </Button>
              </CardFooter>
            </Card>

            {/* Pending Invitations Card */}
            {user !== "loading" && user.invitedOrganizations && user.invitedOrganizations.length > 0 && (
              <Card variant="section">
                <CardHeader>
                  <CardTitle>Pending Invitations</CardTitle>
                  <CardDescription>
                    You have been invited to join {user.invitedOrganizations.length === 1 ? "an organization" : "these organizations"}.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {user.invitedOrganizations.map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{org.displayName}</p>
                          {org.description && (
                            <p className="text-sm text-muted-foreground">{org.description}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleAcceptInvitation(org.id)}
                          disabled={isAcceptingInvitation && acceptingOrgId === org.id}
                        >
                          {isAcceptingInvitation && acceptingOrgId === org.id && (
                            <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          {isAcceptingInvitation && acceptingOrgId === org.id ? "Joining..." : "Accept"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );
      case "organization":
        if (!visibleSections[section]) {
          return (
            <div className="grid gap-6">
              <Card variant="section">
                <CardContent className="p-6">
                  <p className="text-muted-foreground">You don't have access to this section.</p>
                </CardContent>
              </Card>
            </div>
          );
        }

        return (
          <div className="grid gap-6">
                      <Card variant="section">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle>Members</CardTitle>
                  {scimConfigured && <CardDescription>SCIM manages membership. Role changes are available here when provider-managed roles are disabled.</CardDescription>}
                  {features.roles && <RoleHelp />}
                </div>
                <CardDescription>
                  People who have access to this organization.
                </CardDescription>
                {readOnlyNotice("member")}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {canManageMembers && (
                  <div className="space-y-2">
                    <Label htmlFor="inviteEmail">Add Member</Label>
                    <div className="flex gap-2">
                      <Input
                        id="inviteEmail"
                        placeholder="Add by email address..."
                        value={emailToAddToOrg}
                        onChange={(e) => setEmailToAddToOrg(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleInviteMember();
                          }
                        }}
                      />
                      {features.roles && (
                        <RoleSelect
                          value={inviteRole}
                          onChange={setInviteRole}
                          aria-label="Role for the invitee"
                        />
                      )}
                      <Button
                        onClick={handleInviteMember}
                        disabled={isInviting || organization === "loading" || isInvited}
                      >
                        {isInviting && (
                          <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {isInviting ? "Inviting" : isInvited ? "Invited!" : "Invite"}
                      </Button>
                    </div>
                  </div>
                  )}

                  {members?.data === undefined ? (
                    <div className="flex items-center justify-center p-4">
                      <ReloadIcon className="h-5 w-5 animate-spin" />
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {members.data.map((member) => (
                        <SeatRow key={member.id} title={member.displayName || member.name} subtitle={member.emailAddress}>
                          {features.roles && !canManageRoles && (
                            <Badge variant="secondary">{roleLabels[member.role]}</Badge>
                          )}
                          {features.roles && canManageRoles && (
                            <RoleSelect
                              value={member.role}
                              onChange={(role) => handleSetMemberRole(member.emailAddress, role)}
                              disabled={changingRoleOf === member.emailAddress}
                              aria-label={`Role of ${member.emailAddress}`}
                            />
                          )}
                          {canManageMembers && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            aria-label={`Remove ${member.emailAddress}`}
                            onClick={async () => {
                              if (organization === "loading") return;
                              try {
                                const result = await removeMember({ emailAddress: member.emailAddress });
                                if (handleApiError(result, toast, "Member removed successfully.")) {
                                  const me = user !== "loading" ? user : undefined;
                                  const removedMyself = me?.emailAddress === member.emailAddress;
                                  await Promise.all([
                                    mutate(`/organizations/${organization.id}/members`),
                                    // Removing myself changes my own seats and organizations.
                                    removedMyself && me ? mutate(`/users/${me.name}/memberships`) : undefined,
                                    removedMyself ? refreshWhoAmI() : undefined,
                                  ]);
                                }
                              } catch (error) {
                                toast({
                                  title: "Failed to remove member",
                                  description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <MinusCircle className="h-4 w-4" />
                          </Button>
                          )}
                        </SeatRow>
                      ))}
                    </div>
                  )}

                  {invitations?.data && invitations.data.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Invited</p>
                      <div className="grid gap-4">
                        {invitations.data.map((invitation) => (
                          <SeatRow
                            key={invitation.id}
                            dashed
                            title={invitation.email}
                            subtitle={`Invited${features.roles ? ` as ${roleLabels[invitation.role].toLowerCase()}` : ""}, expires ${formatDateLong(invitation.expiresAt)}`}
                          >
                            <Badge variant="outline">Pending</Badge>
                            {canManageMembers && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              aria-label={`Revoke the invitation of ${invitation.email}`}
                              onClick={async () => {
                                try {
                                  handleApiError(await revokeInvitation({ invitationId: invitation.id }), toast, "Invitation revoked.");
                                } catch (error) {
                                  toast({
                                    title: "Failed to revoke invitation",
                                    description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                                    variant: "destructive",
                                  });
                                }
                              }}
                            >
                              <MinusCircle className="h-4 w-4" />
                            </Button>
                            )}
                          </SeatRow>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card variant="section">
              <CardHeader>
                <CardTitle>Organization Settings</CardTitle>
                <CardDescription>
                  Manage your organization settings and preferences.
                </CardDescription>
                {readOnlyNotice("organization")}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="mb-2 block text-sm font-medium text-muted-foreground">Organization ID</Label>
                    <div className="flex gap-2">
                      <code className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-sm">
                        {organization !== "loading" ? organization.tid : ""}
                      </code>
                      <TooltipProvider>
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                if (organization !== "loading") {
                                  navigator.clipboard.writeText(organization.tid);
                                  toast({
                                    description: "Organization ID copied to clipboard",
                                  });
                                }
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Copy ID</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      disabled={!canManageOrganization}
                      value={orgDisplayName !== "loading" ? orgDisplayName : ""}
                      placeholder="Acme Inc"
                      onChange={(e) => {
                        setOrgDisplayName(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      disabled={!canManageOrganization}
                      value={orgDescription !== "loading" ? orgDescription : ""}
                      placeholder=""
                      onChange={(e) => {
                        setOrgDescription(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="concurrencyLimit">Concurrency Limit</Label>
                    <Input
                      id="concurrencyLimit"
                      disabled={!canManageOrganization}
                      type="number"
                      min="0"
                      value={concurrencyLimit !== "loading" ? concurrencyLimit : ""}
                      placeholder="Number of instances"
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value);
                        if (!Number.isNaN(value)) {
                          setConcurrencyLimit(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      {organization !== "loading" ? (
                        <>
                          The maximum is {organization.maxConcurrencyLimit} for this organization. For more, please{" "}
                          <button
                            type="button"
                            className="text-primary hover:underline"
                            onClick={() => setSection("support")}
                          >
                            submit a ticket
                          </button>
                          .
                        </>
                      ) : ""}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Default Inactivity Timeouts (minutes)</p>
                    <Label htmlFor="androidInactivityTimeout">Android</Label>
                    <Input
                      id="androidInactivityTimeout"
                      disabled={!canManageOrganization}
                      type="number"
                      min="0"
                      value={defaultAndroidInactivityTimeout !== "loading" ? defaultAndroidInactivityTimeout : ""}
                      placeholder="Android"
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value);
                        if (!Number.isNaN(value)) {
                          setDefaultAndroidInactivityTimeout(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                    <Label htmlFor="iosInactivityTimeout">iOS</Label>
                    <Input
                      id="iosInactivityTimeout"
                      disabled={!canManageOrganization}
                      type="number"
                      min="0"
                      value={defaultIosInactivityTimeout !== "loading" ? defaultIosInactivityTimeout : ""}
                      placeholder="iOS"
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value);
                        if (!Number.isNaN(value)) {
                          setDefaultIosInactivityTimeout(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                    <Label htmlFor="xcodeInactivityTimeout">Xcode</Label>
                    <Input
                      id="xcodeInactivityTimeout"
                      disabled={!canManageOrganization}
                      type="number"
                      min="0"
                      value={defaultXcodeInactivityTimeout !== "loading" ? defaultXcodeInactivityTimeout : ""}
                      placeholder="Xcode"
                      onChange={(e) => {
                        const value = Number.parseInt(e.target.value);
                        if (!Number.isNaN(value)) {
                          setDefaultXcodeInactivityTimeout(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSaveOrganization();
                        }
                      }}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Android, iOS, and Xcode instances stop after their configured period of inactivity.
                    </p>
                  </div>
                  {features.roles && canManageRoles && (
                    <div>
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <Label htmlFor="defaultJoinRole">Default role for new members</Label>
                        <RoleHelp />
                      </div>
                      <RoleSelect
                        id="defaultJoinRole"
                        value={defaultJoinRole !== "loading" ? defaultJoinRole : undefined}
                        onChange={(role) => setDefaultJoinRole(role as components["schemas"]["JoinRole"])}
                        roles={["member", "viewer"]}
                        disabled={!canManageOrganization}
                        className="w-full"
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        The role for new members and for SCIM reactivation when administrators manage roles. Existing members keep their assigned role.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
              {canManageOrganization && (
              <CardFooter>
                <Button
                  disabled={organization === "loading" || isUpdatingOrganization}
                  onClick={handleSaveOrganization}
                >
                  {isUpdatingOrganization && (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUpdatingOrganization ? "Saving" : "Save changes"}
                </Button>
              </CardFooter>
              )}
            </Card>
          </div>
        );
      case "webhooks":
        return visibleSections.webhooks && organization !== "loading"
          ? <InstanceWebhookSettings organizationId={organization.tid} />
          : null;
      case "sso":
        if (!visibleSections[section]) {
          return (
            <div className="grid gap-6">
              <Card variant="section">
                <CardContent className="p-6">
                  <p className="text-muted-foreground">You don't have access to this section.</p>
                </CardContent>
              </Card>
            </div>
          );
        }

        return (
          <div className="grid gap-6">

            <Card variant="section">
              <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                  <CardTitle>Domain Verification</CardTitle>
                  <CardDescription>
                    {scimConfigured ? "Verified domains require SSO. Membership is managed by SCIM; auto-join is disabled. SCIM requires the configured SSO domains." : "Verify email domains to have everyone join the organization when they sign in."}
                  </CardDescription>
                  {readOnlyNotice("organization")}
                </div>
                {canManageOrganization && (
                <Button
                  variant="outline"
                  size="icon"
                  disabled={scimConfigured}
                  aria-label="Add domain"
                  onClick={handleOpenAddOrganizationDomainDialog}
                >
                  <PlusCircle className="h-4 w-4" />
                </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {organizationDomains.length > 0 || offerSuggestedDomain ? (
                    <div className="grid gap-4">
                      {offerSuggestedDomain && (
                        <div className="rounded-lg border p-4">
                          <div className="flex items-center justify-between space-x-4">
                            <div>
                              <p className="text-sm font-medium leading-none">{suggestedOrganizationDomain}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                onClick={handleOpenSuggestedOrganizationDomainDialog}
                              >
                                Verify
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {organizationDomains.map((domainDraft) => {
                        const domainState = organizationDomainsResult?.data?.find((d) => d.domain === domainDraft.domain)
                          ?? (organizationDomainDialogRecord?.domain === domainDraft.domain ? organizationDomainDialogRecord : undefined);
                        const isVerified = domainState?.verifiedAt !== null && domainState?.verifiedAt !== undefined;
                        return (
                          <div
                            key={domainDraft.domain}
                            className="space-y-3 rounded-lg border p-4"
                          >
                            <div className="flex items-center justify-between space-x-4">
                              <div>
                                <p className="text-sm font-medium leading-none">{domainDraft.domain}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {isVerified || !canManageOrganization ? (
                                  <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
                                    <span className={`h-2 w-2 rounded-full ${isVerified ? "bg-green-500" : "bg-yellow-500"}`} />
                                    {isVerified ? "Verified" : "Pending verification"}
                                  </div>
                                ) : (
                                  <Button
                                    variant="outline"
                                    disabled={verifyingOIDCDomain === domainDraft.domain || !domainState}
                                    onClick={() => handleOpenVerifyOrganizationDomainDialog(domainDraft.domain)}
                                  >
                                    <span className="mr-2 h-2 w-2 rounded-full bg-yellow-500" />
                                    Pending verification
                                  </Button>
                                )}
                                {canManageOrganization && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive hover:text-destructive"
                                  disabled={scimConfigured}
                                  aria-label={`Remove ${domainDraft.domain}`}
                                  onClick={() => handleRemoveOrganizationDomain(domainDraft.domain)}
                                >
                                  <MinusCircle className="h-4 w-4" />
                                </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                      No domains verified yet.
                    </div>
                  )}
                </div>
              </CardContent>
              <Dialog
                open={isOrganizationDomainDialogOpen}
                onOpenChange={(open) => {
                  setIsOrganizationDomainDialogOpen(open);
                  if (!open) {
                    setNewOrganizationDomain("");
                    setOrganizationDomainDialogRecord(undefined);
                    setCopiedOrganizationDomainField(null);
                  }
                }}
              >
                <DialogContent className="sm:max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>
                      {selectedOrganizationDomain ? "Verify domain" : "Add domain"}
                    </DialogTitle>
                    <DialogDescription>
                      Create the TXT record below to verify ownership.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="organizationDomainDialog">Domain</Label>
                      {selectedOrganizationDomain ? (
                        <div
                          id="organizationDomainDialog"
                          className="rounded-md border bg-muted px-3 py-2 font-mono text-sm"
                        >
                          {newOrganizationDomain}
                        </div>
                      ) : (
                        <Input
                          id="organizationDomainDialog"
                          placeholder={userEmailDomain || "example.com"}
                          value={newOrganizationDomain}
                          onChange={(e) => setNewOrganizationDomain(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddOrganizationDomain();
                            }
                          }}
                        />
                      )}
                    </div>

                    {selectedOrganizationDomain && !isSelectedOrganizationDomainVerified && (
                      <div className="space-y-4 rounded-md bg-muted p-4">
                        <p className="text-sm text-muted-foreground">
                          Create this DNS record at your DNS provider, then click Verify DNS.
                        </p>
                        <div className="space-y-3">
                          <div className="grid gap-2 sm:grid-cols-[6rem_1fr] sm:items-center">
                            <Label>Type</Label>
                            <div className="w-fit rounded-md border bg-background px-3 py-1.5 font-mono text-sm">
                              TXT
                            </div>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-[6rem_1fr] sm:items-center">
                            <Label>Host / Name</Label>
                            <div className="relative min-w-0">
                              <button
                                type="button"
                                className="w-full overflow-x-auto whitespace-nowrap rounded-md border bg-background px-3 py-2 text-left font-mono text-sm hover:bg-muted"
                                onClick={() => handleCopyOrganizationDomainField("name", selectedOrganizationDomain.verificationTxtName)}
                              >
                                {selectedOrganizationDomain.verificationTxtName}
                              </button>
                              {copiedOrganizationDomainField === "name" && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded bg-foreground px-2 py-0.5 text-xs text-background shadow">
                                  Copied
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-[6rem_1fr] sm:items-center">
                            <Label>Value</Label>
                            <div className="relative min-w-0">
                              <button
                                type="button"
                                className="w-full overflow-x-auto whitespace-nowrap rounded-md border bg-background px-3 py-2 text-left font-mono text-sm hover:bg-muted"
                                onClick={() => handleCopyOrganizationDomainField("value", selectedOrganizationDomain.verificationTxtValue)}
                              >
                                {selectedOrganizationDomain.verificationTxtValue}
                              </button>
                              {copiedOrganizationDomainField === "value" && (
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 rounded bg-foreground px-2 py-0.5 text-xs text-background shadow">
                                  Copied
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {selectedOrganizationDomainHostPrefix && (
                          <p className="text-sm text-muted-foreground">
                            Some DNS providers only need the host prefix:{" "}
                            <code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs">
                              {selectedOrganizationDomainHostPrefix}
                            </code>
                          </p>
                        )}
                      </div>
                    )}
                    {isSelectedOrganizationDomainVerified && (
                      <div className="flex items-center gap-2 rounded-md border p-3 text-sm text-muted-foreground">
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                        This domain is verified.
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    {selectedOrganizationDomain === undefined ? (
                      <Button
                        disabled={isSavingOrganizationDomains || !normalizedNewOrganizationDomain}
                        onClick={handleAddOrganizationDomain}
                      >
                        {isSavingOrganizationDomains && (
                          <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {isSavingOrganizationDomains ? "Saving" : "Save domain"}
                      </Button>
                    ) : !isSelectedOrganizationDomainVerified ? (
                      <Button
                        disabled={!canManageOrganization || verifyingOIDCDomain === selectedOrganizationDomain.domain}
                        onClick={() => handleVerifyOrganizationDomain(selectedOrganizationDomain.domain)}
                      >
                        {verifyingOIDCDomain === selectedOrganizationDomain.domain && (
                          <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Verify DNS
                      </Button>
                    ) : (
                      <Button onClick={() => setIsOrganizationDomainDialogOpen(false)}>
                        Done
                      </Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </Card>
            <Card variant="section">
              <CardHeader>
                <CardTitle>Single Sign-On</CardTitle>
                <CardDescription>
                  Configure sign-in with an OIDC provider such as Okta, Azure AD, or Google.
                  Once configured, users with a verified domain must use SSO, including existing users and new signups.
                </CardDescription>
                {readOnlyNotice("organization")}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="oidcRedirectUrl">Redirect URI</Label>
                    <div className="flex gap-2">
                      <code
                        id="oidcRedirectUrl"
                        className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border bg-muted px-3 py-2 font-mono text-sm"
                      >
                        {oidcRedirectUrl}
                      </code>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          await navigator.clipboard.writeText(oidcRedirectUrl);
                          setCopiedOIDCRedirect(true);
                          setTimeout(() => setCopiedOIDCRedirect(false), 2000);
                        }}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        {copiedOIDCRedirect ? "Copied" : "Copy"}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="oidcPostLogoutRedirectUrl">Post Logout Redirect URI</Label>
                    <div className="flex gap-2">
                      <code
                        id="oidcPostLogoutRedirectUrl"
                        className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border bg-muted px-3 py-2 font-mono text-sm"
                      >
                        {oidcPostLogoutRedirectUrl}
                      </code>
                      <Button
                        variant="outline"
                        onClick={async () => {
                          await navigator.clipboard.writeText(oidcPostLogoutRedirectUrl);
                          setCopiedOIDCPostLogoutRedirect(true);
                          setTimeout(() => setCopiedOIDCPostLogoutRedirect(false), 2000);
                        }}
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        {copiedOIDCPostLogoutRedirect ? "Copied" : "Copy"}
                      </Button>
                    </div>
                  </div>
                  <fieldset disabled={!canManageOrganization} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="oidcIssuerUrl">Issuer URL</Label>
                    <Input
                      id="oidcIssuerUrl"
                      placeholder="https://company.okta.com"
                      value={oidcIssuerUrl}
                      onChange={(e) => setOIDCIssuerUrl(e.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="oidcClientId">Client ID</Label>
                      <Input
                        id="oidcClientId"
                        value={oidcClientId}
                        onChange={(e) => setOIDCClientId(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="oidcClientSecret">Client Secret</Label>
                      <Input
                        id="oidcClientSecret"
                        type="password"
                        placeholder={oidcConnectionResult?.data?.clientSecretConfigured ? "Leave blank to keep existing secret" : ""}
                        value={oidcClientSecret}
                        onChange={(e) => setOIDCClientSecret(e.target.value)}
                      />
                    </div>
                  </div>
                  </fieldset>
                </div>
              </CardContent>
              {canManageOrganization && (
              <CardFooter className="gap-2">
                <Button
                  disabled={
                    isSavingOIDCConnection ||
                    organization === "loading" ||
                    !oidcIssuerUrl.trim() ||
                    !oidcClientId.trim() ||
                    (!oidcConnectionResult?.data && !oidcClientSecret.trim())
                  }
                  onClick={handleSaveOIDCConnection}
                >
                  {isSavingOIDCConnection && (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isSavingOIDCConnection ? "Saving" : "Save"}
                </Button>
                {oidcConnectionResult?.data && (
                  <Button
                    variant="destructive"
                    disabled={isDeletingOIDCConnection || scimConfigured}
                    onClick={handleDeleteOIDCConnection}
                  >
                    {isDeletingOIDCConnection && (
                      <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Delete
                  </Button>
                )}
              </CardFooter>
              )}
            </Card>

            {canManageOrganization && organizationId !== "loading" && <SCIMProvisioning key={organizationId} organizationId={organizationId} />}

          </div>
        );
      case "support":
        return (
          <div className="grid gap-6">
            <Card variant="section" x-chunk="dashboard-04-chunk-1">
              <CardHeader>
                <CardTitle>Let us help you</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid w-full gap-2">
                  <Textarea
                    onChange={(e) => {
                      setSupportMessage(e.target.value);
                    }}
                    placeholder="Type your message here."
                  />
                  {supportMessageSubmissionResult && (
                    <div className="text-sm text-muted-foreground">{supportMessageSubmissionResult}</div>
                  )}
                  <Button
                    disabled={supportMessageLoading}
                    onClick={async () => {
                      if (!supportMessage) {
                        return;
                      }
                      setSupportMessageLoading(true);
                      try {
                        const result = await sendSupportMessage({
                          message: supportMessage,
                        });
                        if (handleApiError(result, toast, "Message sent successfully.")) {
                          setSupportMessageSubmissionResult(
                            `Thanks! We'll reach out to you at ${user !== "loading" ? user.emailAddress : "your email address"}.`
                          );
                        }
                      } catch (error) {
                        toast({
                          title: "Failed to send message",
                          description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                          variant: "destructive",
                        });
                      } finally {
                        setSupportMessageLoading(false);
                      }
                    }}
                  >
                    {supportMessageLoading ? <ReloadIcon className="animate-spin h-5 w-5 mr-2" /> : ""}
                    Send message
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case "nodePools":
        if (organization === "loading" || !visibleSections[section]) {
          return null;
        }
        return (
          <div className="grid gap-6">
            <NodePools
              key={organization.tid}
              organizationId={organization.tid}
              poolLimit={organization.nodePoolLimit}
              canManage={can("nodepool", "all")}
              onContactUs={() => setSection("support")}
              readOnlyNotice={readOnlyNotice("nodepool")}
            />
          </div>
        );
      case "tokens":
        if (!visibleSections[section]) {
          return (
            <div className="grid gap-6">
              <Card variant="section">
                <CardContent className="p-6">
                  <p className="text-muted-foreground">You don't have access to this section.</p>
                </CardContent>
              </Card>
            </div>
          );
        }
        return (
          <div className="grid gap-6">
            <Card variant="section">
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>
                  Create and manage API keys for programmatic access to {organization !== "loading" ? organization.displayName : ""}.
                </CardDescription>
                {readOnlyNotice("apikey")}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Dialog open={isDialogOpen} onOpenChange={(open) => {
                    if (!open) {
                      setDialogToken(undefined);
                    }
                    setIsDialogOpen(open);
                  }}>
                    {canManageKeys && (
                    <DialogTrigger asChild>
                      <Button onClick={() => {
                        setDialogToken(undefined);
                        setIsDialogOpen(true);
                      }}>Create API key</Button>
                    </DialogTrigger>
                    )}
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Create API key</DialogTitle>
                        <DialogDescription>
                          {dialogToken
                            ? <>You can use <code>LIM_API_KEY</code> environment variable for lim CLI to use this API key.</>
                            : "Create a new API key for programmatic access."
                          }
                        </DialogDescription>
                      </DialogHeader>
                      {dialogToken ? (
                        <div>
                          <ShownOnceSecret value={dialogToken} label="Copy the API key" />
                          <DialogFooter className="mt-4">
                            <Button onClick={() => setIsDialogOpen(false)}>Done</Button>
                          </DialogFooter>
                        </div>
                      ) : (
                        <form onSubmit={async (e) => {
                          e.preventDefault();
                          if (organization === "loading") return;

                          setIsCreatingToken(true);
                          const formData = new FormData(e.currentTarget);
                          const tokenCreate = {
                            description: formData.get("description") as string,
                            expirationMonths: Number.parseInt(formData.get("expiration") as string),
                            // A read-only key holds `*:*:read`; unchecked sends nothing, which
                            // gives the key what the creator's role holds.
                            // Both names preserve restrictions on older backend replicas and after rollback.
                            ...(formData.get("readOnly")
                              ? { permissions: ["*:*:read"], scopes: ["*:*:read"] }
                              : {}),
                          };

                          try {
                            const result = await createToken(tokenCreate);
                            if (handleApiError(result, toast)) {
                              setDialogToken(result?.data?.token);
                            }
                          } catch (error) {
                            toast({
                              title: "Failed to create token",
                              description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                              variant: "destructive",
                            });
                          } finally {
                            setIsCreatingToken(false);
                          }
                        }}>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="description">Description</Label>
                              <Input
                                id="description"
                                name="description"
                                placeholder="API key description (optional)"
                                disabled={isCreatingToken}
                              />
                            </div>
                            <div>
                              <Label htmlFor="expiration">Expiration</Label>
                              <Select
                                name="expiration"
                                defaultValue="3"
                                disabled={isCreatingToken}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select expiration" />
                                </SelectTrigger>
                                <SelectContent>
                                  {keyLifetimeOptions.map((option) => (
                                    <SelectItem key={option.months} value={option.months}>{option.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {(features.apiKeyPermissions ?? features.apiKeyScopes) && (
                              <div className="flex items-start gap-2">
                                <Checkbox id="readOnly" name="readOnly" disabled={isCreatingToken} className="mt-0.5" />
                                <div>
                                  <Label htmlFor="readOnly">Read-only</Label>
                                  <p className="text-sm text-muted-foreground">
                                    Lists and reads only; cannot create, change or stream.
                                  </p>
                                </div>
                              </div>
                            )}
                            <DialogFooter>
                              <Button
                                type="submit"
                                disabled={isCreatingToken}
                              >
                                {isCreatingToken && (
                                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Create API key
                              </Button>
                            </DialogFooter>
                          </div>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>

                  <div className="space-y-4">
                    {tokens === "loading" ? (
                      <div className="flex items-center justify-center p-4">
                        <ReloadIcon className="h-5 w-5 animate-spin" />
                      </div>
                    ) : (
                      tokens.map((token) => {
                        const badge = keyScopeBadge(token.permissions ?? token.scopes);
                        return (
                        <div key={token.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              {token.id}
                              {badge && <Badge variant="secondary">{badge.label}</Badge>}
                            </h4>
                            {token.description && (
                              <p className="text-sm text-muted-foreground">{token.description}</p>
                            )}
                            {badge?.label === "Custom" && (
                              <code className="text-xs text-muted-foreground">{badge.scopes.join(" ")}</code>
                            )}
                            <p className="text-sm text-muted-foreground">
                              {token.revokedAt ? "Revoked" : new Date(token.expiresAt) > new Date() ? "Expires" : "Expired"} at{" "}
                              {formatDateLong(token.revokedAt || token.expiresAt)}
                            </p>
                          </div>
                          {canManageKeys && (
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={!!token.revokedAt || new Date(token.expiresAt) <= new Date()}
                            onClick={async () => {
                              if (organization === "loading") return;
                              const result = await revokeToken(token.id);
                              if (handleApiError(result, toast, "API key deleted successfully.")) {
                                await mutate(`/v1/organizations/${organization.id}/api_keys`);
                              }
                            }}
                          >
                            {token.revokedAt ? "Revoked" : new Date(token.expiresAt) <= new Date() ? "Expired" : "Delete"}
                          </Button>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };
  return (
    <AppShell title="Settings">
      <PageHeader title="Settings" description="Manage your account, organization, and access." actions={<Dialog open={isCreateOrgDialogOpen} onOpenChange={setIsCreateOrgDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <PlusCircle className="h-4 w-4" />
            Create organization
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
            <DialogDescription>
              Create a new organization to manage your resources.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setIsCreatingOrg(true);

            const formData = new FormData(e.currentTarget);
            const orgCreate = {
              displayName: formData.get("name") as string,
              description: formData.get("description") as string,
            };

            try {
              const result = await createOrganization(orgCreate);
              if (handleApiError(result, toast, "Organization created successfully.")) {
                if (result?.data) {
                  // The creator's admin seat is known before the page
                  // switches to the new organization, network or not.
                  await Promise.all([
                    user !== "loading" ? mutate(`/users/${user.name}/memberships`) : undefined,
                    seatCreatedOrganization(result.data),
                  ]);
                  setOrganization(result.data);
                  setIsCreateOrgDialogOpen(false);
                }
              }
            } catch (error) {
              toast({
                title: "Failed to create organization",
                description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
                variant: "destructive",
              });
            } finally {
              setIsCreatingOrg(false);
            }
          }}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Acme Inc"
                  required
                  disabled={isCreatingOrg}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  disabled={isCreatingOrg}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={isCreatingOrg}
              >
                {isCreatingOrg && (
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>} />

      <div className="grid min-w-0 w-full items-start gap-8 md:grid-cols-[180px_minmax(0,1fr)]">
        <nav aria-label="Settings sections" className="flex flex-wrap gap-1 md:flex-col">
          <Button
            variant="link"
            onClick={() => setSection("profile")}
            className={`justify-start px-3 hover:no-underline ${section === "profile" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
          >
            Profile
          </Button>
          {visibleSections.organization && (
            <Button
              variant="link"
              onClick={() => setSection("organization")}
              className={`justify-start px-3 hover:no-underline ${section === "organization" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Organization
            </Button>
          )}
          {visibleSections.sso && (
            <Button
              variant="link"
              onClick={() => setSection("sso")}
              className={`justify-start px-3 hover:no-underline ${section === "sso" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Single Sign-On
            </Button>
          )}
          {visibleSections.nodePools && (
            <Button
              variant="link"
              onClick={() => setSection("nodePools")}
              className={`justify-start px-3 hover:no-underline ${section === "nodePools" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Node pools
            </Button>
          )}
          {visibleSections.webhooks && (
            <Button
              variant="link"
              onClick={() => setSection("webhooks")}
              className={`justify-start px-3 hover:no-underline ${section === "webhooks" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Webhooks
            </Button>
          )}
          {visibleSections.tokens && (
            <Button
              variant="link"
              onClick={() => setSection("tokens")}
              className={`justify-start px-3 hover:no-underline ${section === "tokens" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              API Keys
            </Button>
          )}
          <Button
            variant="link"
            onClick={() => setSection("support")}
            className={`justify-start px-3 hover:no-underline ${section === "support" ? "bg-muted font-semibold text-foreground" : "text-muted-foreground"}`}
          >
            Support
          </Button>
        </nav>
        {content(section)}
      </div>
    </AppShell>
  );
}
